import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Card,
  Text,
  Badge,
  Avatar,
  Skeleton,
  Banner,
  Heading,
  Animate,
  Button,
  Table,
  Grid,
  Modal
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Key20Regular,
  People20Regular,
  Checkmark20Regular,
  Add20Regular,
  Person20Regular,
  Receipt20Regular,
  Calendar20Regular
} from '@fluentui/react-icons'

interface AppSubscription {
  subscription_id: string | null
  app_id: string
  app_name: string
  app_description: string | null
  app_image_url: string | null
  plan: string
  seats_total: number
  seats_used: number
  seats_available: number
  billing_user_id: string | null
  billing_email: string | null
  billing_name: string | null
  status: string
  current_period_start: string | null
  current_period_end: string | null
  cancel_at: string | null
  price_per_seat: number | null
  currency: string
  billing_interval: string
  payment_provider: string | null
  is_mandatory: boolean
  has_sso_connection: boolean
  created_at: string | null
}

interface Seat {
  seat_id: string
  user_id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role: string
  assigned_at: string
  assigned_by_email: string | null
  is_active: boolean
}

interface MemberForSeat {
  user_id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role: string
  has_seat: boolean
}

function getPlanBadgeVariant(plan: string): 'primary' | 'success' | 'warning' | 'default' {
  switch (plan.toLowerCase()) {
    case 'enterprise': return 'primary'
    case 'pro': return 'success'
    case 'starter': return 'warning'
    default: return 'default'
  }
}

function getStatusBadgeVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active': return 'success'
    case 'trialing': return 'warning'
    case 'past_due': return 'error'
    case 'canceled': return 'error'
    default: return 'default'
  }
}

export function OrgAppDetail() {
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const { organization } = useOrg()
  const [subscription, setSubscription] = useState<AppSubscription | null>(null)
  const [seats, setSeats] = useState<Seat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [members, setMembers] = useState<MemberForSeat[]>([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    if (appId) {
      loadData()
    }
  }, [organization.id, appId])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load subscription
      const { data: subData, error: subError } = await supabase.rpc('get_org_app_subscription', {
        p_organization_id: organization.id,
        p_app_id: appId
      })

      if (subError) throw subError
      if (subData && subData.length > 0) {
        setSubscription(subData[0])
      }

      // Load seats
      const { data: seatsData, error: seatsError } = await supabase.rpc('get_org_app_seats', {
        p_organization_id: organization.id,
        p_app_id: appId
      })

      if (seatsError) throw seatsError
      setSeats(seatsData || [])

    } catch (err) {
      console.error('Error loading app detail:', err)
      setError(err instanceof Error ? err.message : 'Failed to load app details')
    } finally {
      setLoading(false)
    }
  }

  const loadMembers = async () => {
    try {
      setLoadingMembers(true)
      const { data, error } = await supabase.rpc('get_org_members_for_seat', {
        p_organization_id: organization.id,
        p_app_id: appId
      })

      if (error) throw error
      setMembers(data || [])
    } catch (err) {
      console.error('Error loading members:', err)
    } finally {
      setLoadingMembers(false)
    }
  }

  const handleOpenAssignModal = () => {
    setShowAssignModal(true)
    loadMembers()
  }

  const handleAssignSeat = async (userId: string) => {
    try {
      setAssigning(true)
      const { error } = await supabase.rpc('assign_org_app_seat', {
        p_organization_id: organization.id,
        p_app_id: appId,
        p_user_id: userId
      })

      if (error) throw error

      setSuccess('Seat assigned successfully')
      loadData()
      loadMembers()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setAssigning(false)
    }
  }

  const handleRevokeSeat = async (userId: string) => {
    try {
      const { error } = await supabase.rpc('revoke_org_app_seat', {
        p_organization_id: organization.id,
        p_app_id: appId,
        p_user_id: userId
      })

      if (error) throw error

      setSuccess('Seat revoked successfully')
      loadData()
    } catch (err: any) {
      setError(err.message)
    }
  }

  if (loading) {
    return (
      <VStack gap="lg">
        <HStack gap="md" style={{ alignItems: 'center' }}>
          <Skeleton width={40} height={40} />
          <VStack gap="xs">
            <Skeleton width={200} height={24} />
            <Skeleton width={300} height={16} />
          </VStack>
        </HStack>
        <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} height={100} />
          ))}
        </Grid>
        <Skeleton height={300} />
      </VStack>
    )
  }

  if (!subscription) {
    return (
      <VStack gap="lg">
        <Button variant="ghost" icon={<ArrowLeft20Regular />} onClick={() => navigate(`/org/${organization.slug}/apps`)}>
          Back to Apps
        </Button>
        <Banner variant="error" title="App not found">
          This application could not be found.
        </Banner>
      </VStack>
    )
  }

  const hasSubscription = subscription.status !== 'none'

  return (
    <VStack gap="lg">
      {/* Header */}
      <Animate type="fadeIn">
        <VStack gap="md">
          <Button variant="ghost" icon={<ArrowLeft20Regular />} onClick={() => navigate(`/org/${organization.slug}/apps`)}>
            Back to Apps
          </Button>

          <HStack gap="lg" style={{ alignItems: 'center' }}>
            <Avatar
              name={subscription.app_name}
              src={subscription.app_image_url || undefined}
              size="xl"
            />
            <VStack gap="xs" style={{ flex: 1 }}>
              <HStack gap="sm" style={{ alignItems: 'center' }}>
                <Heading level={2}>{subscription.app_name}</Heading>
                {subscription.is_mandatory && (
                  <Badge variant="primary">Required</Badge>
                )}
                {hasSubscription && (
                  <Badge variant={getStatusBadgeVariant(subscription.status)}>
                    {subscription.status}
                  </Badge>
                )}
              </HStack>
              {subscription.app_description && (
                <Text color="muted">{subscription.app_description}</Text>
              )}
            </VStack>

            {subscription.has_sso_connection && (
              <Button
                variant="secondary"
                icon={<Key20Regular />}
                onClick={() => navigate(`/org/${organization.slug}/sso`)}
              >
                SSO Configured
              </Button>
            )}
          </HStack>
        </VStack>
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {success && (
        <Banner variant="success" title="Success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      {/* Subscription Info */}
      <Animate type="slideInUp" delay={50}>
        <Card padding="lg">
          <VStack gap="md">
            <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Heading level={4}>Subscription</Heading>
              <Badge variant={getPlanBadgeVariant(subscription.plan)} size="lg">
                {subscription.plan.toUpperCase()}
              </Badge>
            </HStack>

            <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
              <Card padding="md" variant="subtle">
                <VStack gap="xs">
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text size="sm" color="muted">Seats</Text>
                    <People20Regular style={{ color: 'var(--brand-primary)' }} />
                  </HStack>
                  <Text size="lg" weight="bold">{subscription.seats_used}/{subscription.seats_total || '∞'}</Text>
                  {subscription.seats_available > 0 && <Text size="sm" color="muted">{subscription.seats_available} available</Text>}
                </VStack>
              </Card>
              <Card padding="md" variant="subtle">
                <VStack gap="xs">
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text size="sm" color="muted">Price per Seat</Text>
                    <Receipt20Regular style={{ color: 'var(--brand-primary)' }} />
                  </HStack>
                  <Text size="lg" weight="bold">{subscription.price_per_seat ? `${subscription.price_per_seat} ${subscription.currency}` : 'Free'}</Text>
                  <Text size="sm" color="muted">{subscription.billing_interval}</Text>
                </VStack>
              </Card>
              <Card padding="md" variant="subtle">
                <VStack gap="xs">
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text size="sm" color="muted">Billing Contact</Text>
                    <Person20Regular style={{ color: 'var(--brand-primary)' }} />
                  </HStack>
                  <Text size="lg" weight="bold">{subscription.billing_name || subscription.billing_email || '-'}</Text>
                </VStack>
              </Card>
              <Card padding="md" variant="subtle">
                <VStack gap="xs">
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text size="sm" color="muted">Period End</Text>
                    <Calendar20Regular style={{ color: 'var(--brand-primary)' }} />
                  </HStack>
                  <Text size="lg" weight="bold">{subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('en-US') : '-'}</Text>
                  {subscription.cancel_at && <Text size="sm" color="muted">Cancels {new Date(subscription.cancel_at).toLocaleDateString('en-US')}</Text>}
                </VStack>
              </Card>
            </Grid>

            {!hasSubscription && (
              <Banner variant="warning" title="No Subscription">
                This app has no active subscription. Contact your administrator to set up billing.
              </Banner>
            )}
          </VStack>
        </Card>
      </Animate>

      {/* Seats Management */}
      <Animate type="slideInUp" delay={100}>
        <Table
          title="Assigned Seats"
          columns={[
            {
              key: 'user',
              header: 'User',
              render: (_, row: Seat) => (
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Avatar
                    name={row.display_name || row.email}
                    src={row.avatar_url || undefined}
                    size="sm"
                  />
                  <VStack gap="none">
                    <Text weight="medium">{row.display_name || row.email}</Text>
                    <Text size="sm" color="muted">{row.email}</Text>
                  </VStack>
                </HStack>
              )
            },
            {
              key: 'role',
              header: 'Role',
              render: (_, row: Seat) => (
                <Badge variant="default">{row.role}</Badge>
              )
            },
            {
              key: 'assigned_at',
              header: 'Assigned',
              render: (_, row: Seat) => (
                <VStack gap="none">
                  <Text size="sm">{new Date(row.assigned_at).toLocaleDateString('en-US')}</Text>
                  {row.assigned_by_email && (
                    <Text size="sm" color="muted">by {row.assigned_by_email}</Text>
                  )}
                </VStack>
              )
            },
            {
              key: 'status',
              header: 'Status',
              render: (_, row: Seat) => (
                <Badge variant={row.is_active ? 'success' : 'default'}>
                  <HStack gap="xs" style={{ alignItems: 'center' }}>
                    <Checkmark20Regular style={{ fontSize: '12px' }} />
                    <span>Active</span>
                  </HStack>
                </Badge>
              )
            },
            {
              key: 'actions',
              header: '',
              render: (_, row: Seat) => (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleRevokeSeat(row.user_id)}
                >
                  Revoke
                </Button>
              )
            }
          ]}
          data={seats}
          globalActions={
            <Button
              size="sm"
              icon={<Add20Regular />}
              onClick={handleOpenAssignModal}
              disabled={subscription.seats_total > 0 && subscription.seats_available <= 0}
            >
              Assign Seat
            </Button>
          }
        />
      </Animate>

      {/* Assign Seat Modal */}
      <Modal
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title="Assign Seat"
      >
        <VStack gap="md">
          <Text color="muted">
            Select members to give them access to {subscription.app_name}.
            {subscription.seats_total > 0 && (
              <> {subscription.seats_available} seat{subscription.seats_available !== 1 ? 's' : ''} available.</>
            )}
          </Text>

          {loadingMembers ? (
            <VStack gap="sm">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} height={50} />
              ))}
            </VStack>
          ) : (
            <VStack gap="sm">
              {members.map(member => (
                <Card key={member.user_id} padding="sm">
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Avatar
                        name={member.display_name || member.email}
                        src={member.avatar_url || undefined}
                        size="sm"
                      />
                      <VStack gap="none">
                        <Text weight="medium">{member.display_name || member.email}</Text>
                        <Text size="sm" color="muted">{member.email}</Text>
                      </VStack>
                    </HStack>

                    {member.has_seat ? (
                      <Badge variant="success">
                        <HStack gap="xs" style={{ alignItems: 'center' }}>
                          <Checkmark20Regular style={{ fontSize: '12px' }} />
                          <span>Has Seat</span>
                        </HStack>
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleAssignSeat(member.user_id)}
                        loading={assigning}
                        disabled={subscription.seats_total > 0 && subscription.seats_available <= 0}
                      >
                        Assign
                      </Button>
                    )}
                  </HStack>
                </Card>
              ))}

              {members.length === 0 && (
                <Text color="muted">No members available to assign.</Text>
              )}
            </VStack>
          )}

          <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
              Close
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </VStack>
  )
}
